import { assertManifestsAreConsistent, type ModuleManifest, type PermissionKeysOf } from '../types.js'
import { aiModule } from './ai.js'
import { biModule } from './bi.js'
import { coreModule } from './core.js'
import { crmModule } from './crm.js'
import { docsModule } from './docs.js'
import { deliveryModule } from './delivery.js'
import { warehouseModule } from './warehouse.js'
import { erpModule } from './erp.js'
import { hrModule } from './hr.js'
import { mgpWebsiteModule } from './mgp-website.js'
import { partnersModule } from './partners.js'
import { timesheetModule } from './timesheet.js'
import { websiteModule } from './website.js'
import { xypModule } from './xyp.js'

/**
 * Every module the platform ships with. Adding one is a single entry here plus
 * its API and panel folders — see the "Модуль нэмэх" recipe in the README.
 *
 * Order matters only for display: the sidebar renders module navigation in this
 * order, and `core` should stay first.
 */
export const MODULE_MANIFESTS: readonly ModuleManifest[] = [
  coreModule,
  hrModule,
  timesheetModule,
  erpModule,
  crmModule,
  docsModule,
  warehouseModule,
  deliveryModule,

  /*
   * ! BI нь дүгнэдэг модулиудынхаа ДАРАА зогсоно. Энэ жагсаалтын дараалал нь
   * ! зөвхөн харагдацынх ч гэсэн сэлгэгч дээр «эх өгөгдөл → түүний шинжилгээ»
   * ! гэсэн дараалал уншигдах нь хэрэглэгчийн ойлголттой нийцнэ.
   */
  biModule,
  partnersModule,
  websiteModule,
  mgpWebsiteModule,

  /*
   * ! ХУР нь ХН-ийн ДАРАА зогсоно. Модулийн сэлгэгч дээр «ажилтан → түүний
   * ! мэдээллийг улсын бүртгэлээс тулгах» дараалал уншигдана.
   */
  xypModule,

  /*
   * ! AI нь BI-ийн (дараа нь Худалдан авалтын) API-г ДУУДДАГ тул хамгийн СҮҮЛД — «эх өгөгдөл → шинжилгээ → асуулт».
   */
  aiModule,
]

assertManifestsAreConsistent(MODULE_MANIFESTS)

/**
 * The exact set of permission keys the platform knows about, derived from the
 * manifests rather than restated. `can('users:lst')` is a compile error; adding
 * a permission to a manifest makes it available everywhere with no second edit.
 *
 * A new module must be unioned in here as well as added to MODULE_MANIFESTS.
 */
export type PlatformPermission =
  | PermissionKeysOf<typeof aiModule.permissions>
  | PermissionKeysOf<typeof biModule.permissions>
  | PermissionKeysOf<typeof coreModule.permissions>
  | PermissionKeysOf<typeof crmModule.permissions>
  | PermissionKeysOf<typeof docsModule.permissions>
  | PermissionKeysOf<typeof warehouseModule.permissions>
  | PermissionKeysOf<typeof deliveryModule.permissions>
  | PermissionKeysOf<typeof erpModule.permissions>
  | PermissionKeysOf<typeof hrModule.permissions>
  | PermissionKeysOf<typeof mgpWebsiteModule.permissions>
  | PermissionKeysOf<typeof partnersModule.permissions>
  | PermissionKeysOf<typeof timesheetModule.permissions>
  | PermissionKeysOf<typeof websiteModule.permissions>
  | PermissionKeysOf<typeof xypModule.permissions>

/** Every declared key, as data — for seeding and for "grant everything" roles. */
export const ALL_PERMISSION_KEYS: string[] = MODULE_MANIFESTS.flatMap(manifest =>
  manifest.permissions.map(permission => `${permission.resource}:${permission.action}`)
)

export const moduleByKey = (key: string): ModuleManifest | undefined =>
  MODULE_MANIFESTS.find(manifest => manifest.key === key)

export {
  aiModule,
  biModule,
  coreModule,
  crmModule,
  deliveryModule,
  docsModule,
  erpModule,
  hrModule,
  mgpWebsiteModule,
  partnersModule,
  timesheetModule,
  warehouseModule,
  websiteModule,
  xypModule
}
